/**
 * GroupsPanel.tsx — the second vertical panel (#4).
 *
 * Lists every group label in use across the bundle's edges. Each row:
 *   [ eye | target | Group Name | color dots (this group's edge colours) ]
 *
 *  - eye    : hide/show all edges in the group (toggles `hidden`)
 *  - target : highlight the group's edges when toggled on (sets `highlighted`)
 *  - hover  : temporarily highlights the group's edges (preview)
 *
 * Panel top: a search input (filters groups by name) + an "eye-all" button
 * that toggles every group's visibility on/off.
 *
 * The panel is purely presentational: it reports visibility + highlight state
 * up to the App via the callbacks; the App threads those down to GraphCanvas
 * which actually dims/hides the edges.
 */
import { useMemo, useState } from 'react';
import { Eye, EyeSlash, Target, MagnifyingGlass } from '@phosphor-icons/react';

import type { GraphEdge, ScenarioBundle } from '../engine/types';
import { flowKindColor } from '../graph/palette';

interface GroupsPanelProps {
  bundle: ScenarioBundle;
  /** Set of hidden group labels — edges in these groups are dimmed to nothing. */
  hiddenGroups: Set<string>;
  /** Set of highlighted group labels — these groups' edges get a glow ring. */
  highlightedGroups: Set<string>;
  /** The group currently being hover-previewed (highlight-on-hover). */
  hoverGroup: string | null;
  onToggleHidden: (group: string) => void;
  onToggleHighlighted: (group: string) => void;
  onHoverGroup: (group: string | null) => void;
  /** Fire when the user clicks the "eye" in the panel header (hide = true). */
  onToggleAll: (hide: boolean) => void;
  children?: React.ReactNode;
  onClose?: () => void;
}

export function GroupsPanel({
  bundle,
  hiddenGroups,
  highlightedGroups,
  onToggleHidden,
  onToggleHighlighted,
  onHoverGroup,
  onToggleAll,
  children,
}: GroupsPanelProps): JSX.Element {
  const [query, setQuery] = useState('');

  // Build the {group → edges} index once per bundle. Edges without a group
  // are bucketed under "(ungrouped)" so they're addressable too.
  const groups = useMemo(() => {
    const map = new Map<string, GraphEdge[]>();
    const known = new Set<string>(bundle.groups ?? []);
    for (const e of bundle.graph.edges) {
      const g = e.group ?? '(ungrouped)';
      known.add(g);
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(e);
    }
    // Sort: declared groups first (in their declared order), then any extras.
    const declared = (bundle.groups ?? []).filter((g) => map.has(g));
    const extras = [...map.keys()].filter((g) => !(bundle.groups ?? []).includes(g)).sort();
    return [...new Set([...declared, ...extras])]
      .map((name) => ({ name, edges: map.get(name) ?? [] }))
      .filter((g) => g.edges.length > 0 || (bundle.groups ?? []).includes(g.name));
  }, [bundle]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, query]);

  // Toggle-all: if any group is hidden, show all; else hide all.
  const anyHidden = filtered.some((g) => hiddenGroups.has(g.name));
  const allHidden = filtered.length > 0 && filtered.every((g) => hiddenGroups.has(g.name));

  return (
    <aside className="groups-panel">
      {children}
      <div className="groups-panel-head">
        <div className="groups-panel-search">
          <MagnifyingGlass size={13} />
          <input
            type="text"
            placeholder="filter groups…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn-icon-mini"
          title={allHidden ? 'Show all groups' : 'Hide all groups'}
          onClick={() => onToggleAll?.(!allHidden)}
        >
          {allHidden ? <EyeSlash size={14} /> : <Eye size={14} />}
        </button>
      </div>
      <div className="groups-panel-list">
        {filtered.map(({ name, edges }) => {
          const hidden = hiddenGroups.has(name);
          const highlighted = highlightedGroups.has(name);
          // The colour dots: distinct flowKind colours among this group's edges.
          const colors = distinctEdgeColors(edges);
          return (
            <div
              key={name}
              className={`group-row${hidden ? ' is-hidden' : ''}${highlighted ? ' is-highlighted' : ''}`}
              onMouseEnter={() => onHoverGroup(name)}
              onMouseLeave={() => onHoverGroup(null)}
            >
              <button
                type="button"
                className="group-row-icon"
                title={hidden ? 'Show group' : 'Hide group'}
                onClick={() => onToggleHidden(name)}
              >
                {hidden ? <EyeSlash size={14} /> : <Eye size={14} />}
              </button>
              <button
                type="button"
                className={`group-row-icon${highlighted ? ' on' : ''}`}
                title={highlighted ? 'Stop highlighting' : 'Highlight group'}
                onClick={() => onToggleHighlighted(name)}
              >
                <Target size={14} />
              </button>
              <span className="group-row-name" title={name}>{name}</span>
              <span className="group-row-dots">
                {colors.map((c) => (
                  <span key={c} className="group-dot" style={{ background: c }} title={c} />
                ))}
                <span className="group-count">{edges.length}</span>
              </span>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="groups-panel-empty">
            {groups.length === 0
              ? 'No groups yet. Open an edge and add one with [+ group].'
              : `No groups match “${query}”.`}
          </div>
        )}
      </div>
      {/* Hint that toggle-all fires when nothing matches the filter is unhelpful —
          but the anyHidden/allHidden state still reflects the visible subset. */}
      <div className="groups-panel-foot">
        {anyHidden ? `${hiddenGroups.size} hidden` : `${groups.length} group${groups.length === 1 ? '' : 's'}`}
      </div>
    </aside>
  );
}

/** Distinct flowKind colours among a set of edges — for the row's color dots. */
function distinctEdgeColors(edges: GraphEdge[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of edges) {
    const c = flowKindColor(e.flowKind, e.customKind, e.customColor);
    if (!seen.has(c)) {
      seen.add(c);
      out.push(c);
    }
  }
  return out.slice(0, 6);
}
